import ReactECharts from 'echarts-for-react';
import stackData from '../data/stackDependence.json';
import { useIsNarrow } from '../../hooks/useIsNarrow';

/**
 * AsmlChinaShareChart — China's share of ASML equipment sales across three
 * quarters (Fig 2). Bar, not line: three discrete reporting periods, and the
 * shape (fall, then partial recovery, still below a year earlier) is the point.
 * Brand orange marks the current quarter; grey marks the two comparators.
 */

const BRAND = {
  orange: '#FD9800',
  muted: '#9CA3AF',
  mutedDark: '#6B7280',
  surface: '#171717',
};

type ShareItem = { period: string; sharePct: number; current: boolean };

const fmt = (v: number) => `${v}%`;

export const AsmlChinaShareChart = ({ height = 340 }: { height?: number }) => {
  const narrow = useIsNarrow();
  const fig = (
    stackData as unknown as {
      figures: {
        asml_china_share: {
          source: string;
          note: string;
          unit: string;
          items: ShareItem[];
        };
      };
    }
  ).figures.asml_china_share;

  const latest = fig.items[fig.items.length - 1];
  const prior = fig.items[fig.items.length - 2];
  const yearAgo = fig.items[0];
  const yoy = latest.sharePct - yearAgo.sharePct;

  const option = {
    backgroundColor: BRAND.surface,
    // Deterministic paint: a chart that animates from zero renders as empty bars
    // in a throttled/background tab, which is how readers open a post from a feed.
    animation: false,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params
          .map((p) => `${p.marker} ${p.name}<br/><b>${fmt(p.value)}</b> of ASML equipment sales`)
          .join(''),
    },
    grid: { left: narrow ? 38 : 44, right: narrow ? 14 : 20, top: 34, bottom: narrow ? 30 : 34 },
    xAxis: {
      type: 'category',
      data: fig.items.map((i) => i.period),
      axisLine: { lineStyle: { color: '#333' } },
      axisLabel: { color: '#e5e5e5', fontSize: narrow ? 11 : 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 50,
      axisLabel: { color: BRAND.muted, fontSize: narrow ? 11 : 12, formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    series: [
      {
        type: 'bar',
        barWidth: '46%',
        data: fig.items.map((i) => ({
          value: i.sharePct,
          itemStyle: {
            color: i.current ? BRAND.orange : i.period === 'Q3 2025' ? BRAND.mutedDark : BRAND.muted,
            borderRadius: [4, 4, 0, 0],
          },
        })),
        label: {
          show: true,
          position: 'top',
          color: '#e5e5e5',
          fontWeight: 700,
          fontSize: narrow ? 12 : 13,
          formatter: (p: { value: number }) => fmt(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: BRAND.orange, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: BRAND.orange,
            fontSize: 11,
            fontWeight: 700,
            formatter: `a year earlier: ${fmt(yearAgo.sharePct)}`,
          },
          data: [{ yAxis: yearAgo.sharePct }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 4. China&apos;s share of ASML equipment sales: {fmt(latest.sharePct)} in {latest.period}, up from{' '}
        {fmt(prior.sharePct)} the previous quarter and {Math.abs(yoy)} points below a year earlier (
        {fmt(yearAgo.sharePct)}). {fig.note}
      </p>
    </div>
  );
};
